import { deepMerge } from "../seller/lib/deep-merge";

/**
 * 
 * @typedef {Object} Share
 * @property {string} medium
 * @property {boolean} app
 * @property {'pdf' | 'cart'} clipboard
 * @property {boolean} download
 * 
 * @typedef {Object} Tooltips
 * @property {boolean} quoteNoProducts
 * 
 * @typedef {Object} SuggestedMessage
 * @property {string} label
 * @property {boolean} enable_user_instruction
 * 
 * @typedef {Object} SellerDetails
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} id
 * @property {string} username
 * @property {string} countryCode
 * @property {string} langCode
 */

/**
 * @typedef {Object} SessionDetails
 * @property {string} tcc
 * @property {string} currentCartId
 * @property {string} currentQuoteId
 * @property {string} currentQuoteState
 * @property {string} currentQuoteNumber
 * @property {Tooltips} tooltips
 * @property {Share} share
 * @property {Array} customerGroups
 * @property {Array} templates
 * @property {string} currentCartVersion
 * @property {string} quotedCartId
 * @property {string} quotedCartVersion
 * @property {string} ahiaSessionId
 * @property {string} currencyCode
 * @property {SuggestedMessage} suggestedMessage
 */

/**
 * @typedef {Object} SessionData
 * @property {SellerDetails} sellerDetails
 * @property {SessionDetails} sessionDetails
 */

/**
 * @typedef {Object} EventEmitter
 * @property {function(string, function)} on - Subscribe to an event
 * @property {function(string, ...any)} emit - Emit an event
 */

/**
 * @typedef {SellerDetails & SessionDetails} SessionAccessors
 */

/**
 * @typedef {Object} SessionData
 * @property {SellerDetails} sellerDetails
 * @property {SessionDetails} sessionDetails
 */
export default class Session {
    constructor(key) {

        /** @private */
        this.STORAGE_KEY = key;

        /**
         * Event emitter for session changes
         * @type {EventEmitter}
         * @public
         */
        this.hooks = this.createEventEmitter();

        /** @private */
        this.data = this.get();

        this.setupDynamicProperties();

        Session.instance = this;
        return this;
    }

    getBaseSession = () => {
        return {
            sellerDetails: {
                firstName: '',
                lastName: '',
                id: '',
                username: '',
                countryCode: '',
                langCode: 'en-US'
            },
            sessionDetails: {
                tcc: '',
                currentCartId: '',
                currentQuoteId: '',
                currentQuoteState: '',
                currentQuoteNumber: '',
                tooltips: {
                  quoteNoProducts: true,
                },
                share: {
                    medium: 'whatsapp',
                    app: false,
                    clipboard: 'pdf',
                    download: true
                },
                customerGroups: [],
                templates: [],
                shareMediums: {},
                currentCartVersion: '',
                quotedCartId: '',
                quotedCartVersion: '',
                ahiaSessionId: '',
                currencyCode: null,
                suggestedMessage: {
                    label: '',
                    enable_user_instruction: false
                },
            },
        }
    };

    createEventEmitter() {
        const events = new Map();
        return {
            on: (event, callback) => {
                if (!events.has(event)) events.set(event, []);
                events.get(event).push(callback);
            },
            emit: (event, ...args) => {
                events.get(event)?.forEach((callback) => callback(...args));
            },
        };
    }

    /**
     * Get current session data
     * @returns {SessionData}
     */

    get() {
        const base = this.getBaseSession();
        const storedData = localStorage.getItem(this.STORAGE_KEY);

        return storedData
            ? deepMerge(base, JSON.parse(storedData))
            : base
    }

    set(updates = {}) {
        const oldData = this.get();
        const mergedData = {
            sellerDetails: {
                ...oldData.sellerDetails,
                ...updates.sellerDetails,
            },
            sessionDetails: {
                ...oldData.sessionDetails,
                ...updates.sessionDetails,
            },
        };

        this.checkAndEmitEvents(oldData, mergedData);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(mergedData));
        this.data = mergedData;
    }

    checkAndEmitEvents(oldData, newData) {
        const { sellerDetails: oldSeller, sessionDetails: oldSession } =
            oldData;
        const { sellerDetails: newSeller, sessionDetails: newSession } =
            newData;

        this.hooks.emit('seller-state-change', newData);

        this.checkSessionChanges(oldSession, newSession);
    }

    checkSessionChanges(oldSession, newSession) {
        if (oldSession.tcc !== newSession.tcc) {
            this.hooks.emit('tcc-changed', newSession.tcc);
        }
        if (oldSession.currentCartId !== newSession.currentCartId) {
            this.hooks.emit('cart-changed', newSession.currentCartId);
        }
        if (oldSession.currentCartVersion !== newSession.currentCartVersion) {
            this.hooks.emit('cart-version-changed', newSession.currentCartId);
        }
        if (
            oldSession.currentQuoteId !== newSession.currentQuoteId ||
            oldSession.currentQuoteState !== newSession.currentQuoteState ||
            oldSession.currentQuoteNumber !== newSession.currentQuoteNumber
        ) {
            this.hooks.emit('quote-changed', {
                id: newSession.currentQuoteId,
                number: newSession.currentQuoteNumber,
                state: newSession.currentQuoteState,
            });
        }
    }

    remove() {
        localStorage.removeItem(this.STORAGE_KEY);
        this.data = this.get();
    }

    setupDynamicProperties() {
        const keys = [
            'sellerDetails',
            'sessionDetails',
            ...Object.keys(this.data.sellerDetails),
            ...Object.keys(this.data.sessionDetails),
        ];

        keys.forEach((key) => {
            Object.defineProperty(this, key, {
                get: () => {
                    if (this.data.hasOwnProperty(key)) {
                        return this.data[key];
                    } else if (this.data.sellerDetails.hasOwnProperty(key)) {
                        return this.data.sellerDetails[key];
                    } else if (this.data.sessionDetails.hasOwnProperty(key)) {
                        return this.data.sessionDetails[key];
                    } else {
                        return undefined;
                    }
                },
                set: (value) => {
                    const updates = {};
                    if (this.data.hasOwnProperty(key)) {
                        updates[key] = value;
                    } else if (this.data.sellerDetails.hasOwnProperty(key)) {
                        updates.sellerDetails = { [key]: value };
                    } else if (this.data.sessionDetails.hasOwnProperty(key)) {
                        updates.sessionDetails = { [key]: value };
                    }
                    this.set(updates);
                },
                enumerable: true,
            });
        });
    }
}
